// Pairings, mesas, resultados y recálculos.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  RecalculateResultsPreview,
  Tournament,
  TournamentMatch,
  TournamentMatchInput,
  TournamentMatchStatus,
  TournamentPlayer,
  TournamentResult,
  UserWithToken,
  WithData,
} from '../api.types';
import { USER_BASIC_FIELDS, USER_WITH_GAME_IDS } from '../fragments';
import { RoundResultInput } from './public';

export const pairRound = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  tables: TournamentMatchInput[]
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation (
        $tournamentId: Int!
        $round: Int!
        $tables: [TournamentMatchInput!]!
      ) {
        data: pairRound(
          tournamentId: $tournamentId
          round: $round
          tables: $tables
        ) {
          id
        }
      }
    `,
    { tournamentId, round, tables }
  );

export const repairRound = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  autoGeneratePairings: boolean = true
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation repairRound(
        $tournamentId: Int!
        $round: Int!
        $autoGeneratePairings: Boolean
      ) {
        data: repairRound(
          tournamentId: $tournamentId
          round: $round
          autoGeneratePairings: $autoGeneratePairings
        ) {
          id
        }
      }
    `,
    { tournamentId, round, autoGeneratePairings }
  );

export const createMatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  tableNumber: number,
  player1Id: Nullable<number>,
  player2Id: Nullable<number>,
  winnerId: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentMatch>>(
    gql`
      mutation createMatch(
        $tournamentId: Int!
        $round: Int!
        $tableNumber: Int!
        $player1Id: Int
        $player2Id: Int
        $winnerId: Int
      ) {
        data: createMatch(
          tournamentId: $tournamentId
          round: $round
          tableNumber: $tableNumber
          player1Id: $player1Id
          player2Id: $player2Id
          winnerId: $winnerId
        ) {
          id
          round
          tableNumber
          status
        }
      }
    `,
    { tournamentId, round, tableNumber, player1Id, player2Id, winnerId }
  );

export type CreateMatchBatchInput = {
  round: number;
  tableNumber: number;
  player1Id?: number | null;
  player2Id?: number | null;
  winnerId?: number | null;
};

export type CreateMatchesBatchResult = {
  created: Array<{
    id: number;
    round: number;
    tableNumber: number;
    status: TournamentMatchStatus;
  }>;
  errors: Array<{
    tableNumber: number;
    round: number;
    message: string;
  }>;
};

export const createMatchesBatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  matches: CreateMatchBatchInput[]
) =>
  getGraphQLQueryClient(user).request<{ data: CreateMatchesBatchResult }>(
    gql`
      mutation createMatchesBatch(
        $tournamentId: Int!
        $matches: [CreateMatchBatchInput!]!
      ) {
        data: createMatchesBatch(
          tournamentId: $tournamentId
          matches: $matches
        ) {
          created {
            id
            round
            tableNumber
            status
          }
          errors {
            tableNumber
            round
            message
          }
        }
      }
    `,
    { tournamentId, matches }
  );

export const updateMatch = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  player1Id: Nullable<number>,
  player2Id: Nullable<number>
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentMatch>>(
    gql`
      mutation updateMatch($matchId: Int!, $player1Id: Int, $player2Id: Int) {
        data: updateMatch(
          matchId: $matchId
          player1Id: $player1Id
          player2Id: $player2Id
        ) {
          id
          round
          tableNumber
          status
          player1 {
            id
            name
          }
          player2 {
            id
            name
          }
        }
      }
    `,
    { matchId, player1Id, player2Id }
  );

export const swapMatchPlayers = async (
  user: Nullable<UserWithToken>,
  matchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentMatch>>(
    gql`
      mutation swapMatchPlayers($matchId: Int!) {
        data: swapMatchPlayers(matchId: $matchId) {
          id
          round
          tableNumber
          status
          player1 {
            id
            name
          }
          player2 {
            id
            name
          }
        }
      }
    `,
    { matchId }
  );

export const reportMatch = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  winnerId: number,
  replayUrl?: string | null
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation reportMatch($matchId: Int!, $winnerId: Int!, $replayUrl: String) {
        data: reportMatch(matchId: $matchId, winnerId: $winnerId, replayUrl: $replayUrl) {
          id
        }
      }
    `,
    { matchId, winnerId, replayUrl }
  );

/** Ids de los jugadores que marcaron "Partido coordinado" en una ronda. Organización. */
export const getRoundCoordinations = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<WithData<number[]>>(
    gql`
      query tournamentRoundCoordinations($tournamentId: Int!, $round: Int!) {
        data: tournamentRoundCoordinations(tournamentId: $tournamentId, round: $round)
      }
    `,
    { tournamentId, round }
  );

/** La organización marca (o desmarca) una mesa como coordinada. */
export const setTableCoordinated = async (
  user: Nullable<UserWithToken>,
  input: { tournamentId: number; round: number; tableNumber: number; coordinated: boolean }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setTableCoordinated(
        $tournamentId: Int!
        $round: Int!
        $tableNumber: Int!
        $coordinated: Boolean!
      ) {
        data: setTableCoordinated(
          tournamentId: $tournamentId
          round: $round
          tableNumber: $tableNumber
          coordinated: $coordinated
        )
      }
    `,
    input
  );

export const setMatchReplay = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  replayUrl: string | null
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; replayUrl: string | null }>
  >(
    gql`
      mutation setMatchReplay($matchId: Int!, $replayUrl: String) {
        data: setMatchReplay(matchId: $matchId, replayUrl: $replayUrl) {
          id
          replayUrl
        }
      }
    `,
    { matchId, replayUrl }
  );

/**
 * Reencola el archivado de un replay de Dueling Book (Task 9). Pide privilegios
 * de organizador en el API (admin entra por ahí), porque el botón vive en la
 * pantalla de organizador.
 *
 * Es idempotente: si el replay está en `FAILED` o `NOT_FOUND` vuelve a
 * `PENDING` con los contadores limpios, y si está en `DONE` o `CLAIMED` no se
 * toca. Apretar el botón dos veces no rompe nada.
 */
export const enqueueReplay = async (
  user: Nullable<UserWithToken>,
  duelId: number,
  matchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation ($duelId: Int!, $matchId: Int) {
        data: enqueueReplay(duelId: $duelId, matchId: $matchId)
      }
    `,
    { duelId, matchId }
  );

export const createResult = async (
  user: Nullable<UserWithToken>,
  input: {
    tournamentId: number;
    userId: number;
    position: number;
    points: number;
    wins: number;
    draws: number;
    losses: number;
    tiebreaker?: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentResult>>(
    gql`
      mutation createResult($input: CreateTournamentResultInput!) {
        data: createResult(input: $input) {
          id
          userId
        }
      }
    `,
    { input }
  );

export type CreateResultBatchInput = {
  userId: number;
  position: number;
  points: number;
  wins: number;
  losses: number;
  draws: number;
  tiebreaker?: string;
};

export type CreateResultsBatchResult = {
  created: Array<{
    id: number;
    userId: number;
    position: number;
  }>;
  errors: Array<{
    userId: number;
    message: string;
  }>;
};

export const createResultsBatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  results: CreateResultBatchInput[]
) =>
  getGraphQLQueryClient(user).request<{ data: CreateResultsBatchResult }>(
    gql`
      mutation createResultsBatch(
        $tournamentId: Int!
        $results: [CreateResultBatchInput!]!
      ) {
        data: createResultsBatch(
          tournamentId: $tournamentId
          results: $results
        ) {
          created {
            id
            userId
            position
          }
          errors {
            userId
            message
          }
        }
      }
    `,
    { tournamentId, results }
  );

export const clearMatch = async (
  user: Nullable<UserWithToken>,
  matchId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation clearMatch($matchId: Int!) {
        data: clearMatch(matchId: $matchId) {
          id
        }
      }
    `,
    { matchId }
  );

export const reportMatchGame = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  game: number,
  winnerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentMatch>>(
    gql`
      mutation reportMatchGame($matchId: Int!, $game: Int!, $winnerId: Int!) {
        data: reportMatchGame(matchId: $matchId, game: $game, winnerId: $winnerId) {
          id
          game1Winner { id }
          game2Winner { id }
          game3Winner { id }
          game4Winner { id }
          game5Winner { id }
        }
      }
    `,
    { matchId, game, winnerId }
  );

export const clearMatchGame = async (
  user: Nullable<UserWithToken>,
  matchId: number,
  game: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentMatch>>(
    gql`
      mutation clearMatchGame($matchId: Int!, $game: Int!) {
        data: clearMatchGame(matchId: $matchId, game: $game) {
          id
          game1Winner { id }
          game2Winner { id }
          game3Winner { id }
          game4Winner { id }
          game5Winner { id }
        }
      }
    `,
    { matchId, game }
  );

export const finishRound = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  autoGeneratePairings: boolean = true
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation (
        $tournamentId: Int!
        $round: Int!
        $autoGeneratePairings: Boolean
      ) {
        data: finishRound(
          tournamentId: $tournamentId
          round: $round
          autoGeneratePairings: $autoGeneratePairings
        ) {
          id
          currentRound
        }
      }
    `,
    { tournamentId, round, autoGeneratePairings }
  );

export const saveResult = async (
  user: Nullable<UserWithToken>,
  resultId: number,
  tournamentId: number,
  position: number,
  points: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation saveResult($input: TournamentResultInput!) {
        data: saveResult(input: $input) {
          id
        }
      }
    `,
    { input: { tournamentId, resultId, position, points } }
  );

export const saveResults = async (
  user: Nullable<UserWithToken>,
  results: {
    resultId: number;
    tournamentId: number;
    position: number;
    points: number;
    wins: number;
    losses: number;
    draws: number;
  }[]
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      ${USER_WITH_GAME_IDS}
      mutation saveResults($input: [TournamentResultInput!]!) {
        data: saveResults(input: $input) {
          id

          results {
            id
            position
            points
            wins
            losses
            draws
            tiebreaker
            player {
              id
              user {
                ...UserWithGameIds
              }
            }
          }
        }
      }
    `,
    { input: results }
  );

export const recalculatePlayerTiebreaker = async (
  user: Nullable<UserWithToken>,
  resultId: number
) =>
  getGraphQLQueryClient(user).request<{ data: TournamentResult }>(
    gql`
      mutation recalculatePlayerTiebreaker($resultId: Int!) {
        data: recalculatePlayerTiebreaker(resultId: $resultId) {
          id
          position
          points
          wins
          losses
          draws
          tiebreaker
          omwPercent
          oomwPercent
        }
      }
    `,
    { resultId }
  );

export const dropPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  playerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation dropPlayer($tournamentId: Int!, $playerId: Int!) {
        data: dropPlayer(tournamentId: $tournamentId, playerId: $playerId) {
          id
        }
      }
    `,
    { tournamentId, playerId }
  );

export const undropPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  playerId: number,
  roundResults?: RoundResultInput[]
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation undropPlayer($tournamentId: Int!, $playerId: Int!, $roundResults: [RoundResultInput!]) {
        data: undropPlayer(tournamentId: $tournamentId, playerId: $playerId, roundResults: $roundResults) {
          id
        }
      }
    `,
    { tournamentId, playerId, roundResults }
  );

export const replacePlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  oldUserId: number,
  newUserId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation replacePlayer(
        $tournamentId: Int!
        $oldUserId: Int!
        $newUserId: Int!
      ) {
        data: replacePlayer(
          tournamentId: $tournamentId
          oldUserId: $oldUserId
          newUserId: $newUserId
        ) {
          id
        }
      }
    `,
    { tournamentId, oldUserId, newUserId }
  );

export const updateTournamentPlayer = async (
  user: Nullable<UserWithToken>,
  playerId: number,
  input: {
    deckName?: string;
    metaDeckId?: number | null;
    fixedTable?: number | null;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation updateTournamentPlayer(
        $playerId: Int!
        $input: UpdateTournamentPlayerInput!
      ) {
        data: updateTournamentPlayer(playerId: $playerId, input: $input) {
          id
          deckName
          metaDeckId
          fixedTable
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { playerId, input }
  );

export const recalculateResults = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation recalculateResults($tournamentId: Int!) {
        data: recalculateResults(tournamentId: $tournamentId) {
          id
        }
      }
    `,
    { tournamentId }
  );

/**
 * Simula el recálculo sin escribir, para ver qué cambiaría antes de aplicarlo.
 * Es una query, no una mutation: no tiene efectos.
 */
export const getRecalculateResultsPreview = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<RecalculateResultsPreview>>(
    gql`
      query recalculateResultsPreview($tournamentId: Int!) {
        data: recalculateResultsPreview(tournamentId: $tournamentId) {
          tournamentId
          changedCount
          rows {
            playerId
            userId
            changed
            current {
              position
              points
              wins
              losses
              draws
              omwPercent
              oomwPercent
              lossSquaresSum
              tiebreaker
            }
            next {
              position
              points
              wins
              losses
              draws
              omwPercent
              oomwPercent
              lossSquaresSum
              tiebreaker
            }
          }
        }
      }
    `,
    { tournamentId }
  );

export const recalculateWins = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation recalculateWins($tournamentId: Int!) {
        data: recalculateWins(tournamentId: $tournamentId) {
          id
        }
      }
    `,
    { tournamentId }
  );

export const exportTournamentToKTS = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{ data: string }>(
    gql`
      mutation exportTournamentToKTS($tournamentId: Int!) {
        data: exportTournamentToKTS(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );
